import { Component, Input, OnInit, ViewChild, ElementRef, TemplateRef, OnDestroy, ChangeDetectorRef, OnChanges, SimpleChanges, EventEmitter, Output, ViewChildren, QueryList } from '@angular/core';
import { schemeCodeOf } from '../../../../../core/utils/scheme-code';
import { ageFromDob, sexFromPatient, toOnsetDate } from 'src/app/shared/util/icd-coding-context.util';
import { FormArray, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, EMPTY, Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, takeUntil, take, first } from 'rxjs/operators';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { FeedbackService } from 'src/app/core/services/feedback.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MedpraxService, MsrRow, NetRow, PriceGroup, SpaRow } from 'src/app/core/invoicing/medprax.invoicing.service';
import { AppDataService, ProcedureCode, IcdCode } from 'src/app/core/services/app-data.service';
import { UserService } from 'src/app/core/services/user.service';
import { ContactService } from 'src/app/core/services/contact.service';
import { SPECIALITIES, TITLES } from 'src/app/core/data/app.data';
import { response } from 'express';
import { Speciality } from 'src/app/core/models/speciality.model';
import { GroupService } from 'src/app/core/services/group.service';
import { set } from 'lodash';
import { CodingBundleService } from 'src/app/core/services/coding-bundle.service';
import { parse } from 'path';
import { ClaimLine, ModifierContext, CoreModifier, AdjustedLine } from 'src/app/core/modifier-rule-engine/modifier-models';
import { ModifierService } from 'src/app/core/modifier-rule-engine/modifier.service';
import { ConsumablePricingService } from 'src/app/core/consumable-pricing-engine/consumable-pricing.service';
import { ConsumableLine, ConsumablePricingContext } from 'src/app/core/consumable-pricing-engine/consumable-models';
import * as _ from 'lodash';
import { ProcedureCodeSelectorComponent } from 'src/app/shared/components/procedure-code-selector/procedure-code-selector.component';
import { Router } from '@angular/router';
import { AppointmentTypeService } from 'src/app/core/services/appointment-type.service';
import { DisciplineConfigService } from 'src/app/core/services/discipline-config.service';
import { splitInclusive, vatRateForGroup } from 'src/app/core/utils/vat.util';
import { lineProvider, providerLabel } from 'src/app/core/utils/billing-line-provider';
import { AssistantOption } from 'src/app/core/services/billing.service';
import { CodePromptingService, TimeDetails, BMIDetails, AssistantDetails } from 'src/app/core/services/code-prompting.service';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { ThemeService } from 'src/app/core/services/theme.service';
import { lineQuantity, roundMoney } from '../../../../../core/utils/line-quantity';

declare module 'src/app/core/modifier-rule-engine/modifier-models' {
  interface ClaimLine { __formIdx?: number; }
  interface AdjustedLine { __formIdx?: number; }
}

// Add this interface at the top of the file with other interfaces
interface AppliedModifier {
  lineIndex: number;
  modifierCode: string;
  originalUnits: number;
  originalPrice: number;
}

@Component({
  selector: 'app-multiline-items',
  templateUrl: './multiline-items.component.html',
  styleUrls: ['./multiline-items.component.scss']
})
export class MultilineItemsComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {

  // ── ICD-10 coding-rule context (shared/util/icd-coding-context.util.ts) ──────────
  //
  // Feeds the code selector's rules. Each returns null when the value is unknown, and a rule
  // with no context stays SILENT rather than guessing — a false alarm reads as the system not
  // knowing the patient, which costs trust in every other finding.
  get icdCodingSex(): string | null { return sexFromPatient(this.patient); }
  get icdCodingAge(): number | null { return ageFromDob(this.patient?.dob); }

  /** Dates a newly added code from the admission the disputed line belongs to — never from today. */
  get icdDefaultOnsetDate(): string | null { return toOnsetDate(this.patient?.admissionDate); }

  @Input() parentForm!: FormGroup;
  @Input() modifiersOnly: boolean = false;
  @Input() priceGroup: any;
  @Input() issueDate: Date = new Date();
  @Input() ruleType: string = 'OH';
  @Input() selectedAppointment: any;
  @Input() isPMB: boolean = false;
  @Input() isPhysioPractice: boolean = false;
  @Input() multilineItemsTrigger: number = 0;
  /** The practice doctors a line can be billed for, and the document's provider (a line's default). */
  @Input() providers: any[] = [];
  @Input() providerId: string | null = null;
  @Output() updateTotals = new EventEmitter<any>();
  patient: any = {};
  isPrivatePatient: boolean = false;
  isUsingDefaultRates: boolean = false;
  patientIcdCodes: IcdCode[] = []; // Store patient's ICD codes
  icd10codes: any[] = [];
  nappi: any[] = [];
  icdChips: any[] = [];
  speciality: string | undefined;
  appointmentTypes: any[] = [];

  filteredProcedureCodes: Observable<ProcedureCode[]>;
  filteredNappiCodes: Observable<any[]>;
  tariffs: any[] = [];
  nappiProducts: any[] = [];

  separatorKeysCodes: number[] = [ENTER, COMMA];
  addOnBlur = true;
  pcControl = new FormControl();

  @ViewChild('contactDialog') contactDialog?: TemplateRef<any>;
  @ViewChild('ruleSuggestionsDialog') ruleSuggestionsDialog?: TemplateRef<any>;
  ruleDialogRef: MatDialogRef<any> | null = null;
  contactMessage: any = {};
  titles = TITLES;
  planOptionCode: any;
  selectedSpeciality?: Speciality;
  disciplineCode: any;
  tariffRates: any[] = [];
  selectedTariffRateType: string | undefined;
  group!: any;
  groupId!: string;
  discountedPrice: number = 0.00;
  calculatedPrice: number = 0.00;
  calculatedVat: number = 0.00;
  calculatedTotal: number = 0.00;
  overallSubtotal: number = 0;
  overallVat: number = 0;
  overallVatRate: number = 0;
  overallTotal: number = 0;
  overallTotalDue: number = 0;
  overallShortfall: number = 0;
  rateType: string = 'medical';

  // Data for price calculation
  msrData: MsrRow[] = [];
  spaData: SpaRow[] = [];
  netData: NetRow[] = [];
  unitPrice: number = 0;
  cusPrice: number = 0;
  codingBundle: any;

  codingBundles: any = [];
  showLineItems: boolean = false;
  expandedFields: { [key: number]: boolean } = {};

  // Add loading states map
  loadingStates: { [key: number]: boolean } = {};

  // Add discount type tracking
  discountTypes: { [key: number]: 'percentage' | 'fixed' } = {};

  currentIndex: number = 0;

  private modifierEngineSubject = new Subject<number | undefined>();
  private readonly DEBOUNCE_TIME = 1000; // 1s debounce

  // While a bundle is being expanded we don't want the debounced engine to
  // fire — every `handleCodeSelection` / `handleStandaloneModifierSelection`
  // call during expansion would otherwise queue up a run, each triggering
  // further patchValue → valueChanges → runModifierEngine cycles. The bundle
  // flow drives a single synchronous `_runModifierEngine()` call at the end.
  private isBundleExpanding = false;

  // Re-entry guard for `_runModifierEngine`. The engine's own patchValue
  // calls can (despite emitEvent:false) cascade through third-party
  // directives that bypass Angular's suppression. This guard makes the
  // engine atomic — any nested call short-circuits.
  private isEngineRunning = false;

  // Add this property to track applied modifiers
  private appliedModifiers: AppliedModifier[] = [];

  @ViewChildren('procedureCodeSelector') procedureCodeSelectors!: QueryList<ProcedureCodeSelectorComponent>;

  private DEFAULT_PATIENT_RATES = {
    schemeName: 'DISCOVERY',
    schemeCode: 'S0680',
    planCode: 'CLASS100',
    planOptionCode: '95C'
  }

  private DEFAULT_PRICE_GROUP = {
    "priceGroup": {
      "code": "NNW9",
      "description": "DISCOVERY HEALTH (NON-NETWORK)",
      "tariffMedicalTypeCode": "MP",
      "tariffMedicalType": {
        "code": "MP",
        "description": "MP"
      }
    },
    "name": "NNW",
    "modifier": 100,
    "isDefaultRate": false,
    "isNetworkDoctor": false,
  }

  @Input() appointment?: any;
  matchingPriceOverrides: any[] = [];
  selectedPriceOverride?: ProcedureCode;
  private priceOverrideDialogRef?: MatDialogRef<any>;

  constructor(
    private fb: FormBuilder,
    private patientService: PatientService,
    private medpraxService: MedpraxService,
    private appDataService: AppDataService,
    private userService: UserService,
    private groupService: GroupService,
    private contactService: ContactService,
    private codingBundleService: CodingBundleService,
    private modifierService: ModifierService,
    private consumablePricingService: ConsumablePricingService,
    private appointmentTypeService: AppointmentTypeService,
    private disciplineConfigService: DisciplineConfigService,
    private codePromptingService: CodePromptingService,
    private themeService: ThemeService,
    private router: Router,
    fs: FeedbackService,
    md: MatDialog,
    private cdRef: ChangeDetectorRef,
  ) {
    super(fs, md);
    this.filteredProcedureCodes = this.pcControl.valueChanges.pipe(
      startWith(''),
      switchMap(value => this.filterProcedureCodes(value))
    );

    this.filteredNappiCodes = this.pcControl.valueChanges.pipe(
      startWith(''),
      switchMap(value =>
        this.filterNappiCodes(value ? value.toString() : '')
      ),

    );

    this.codingBundleService.codingBundles$.subscribe(bundles => {
      if (bundles) {
        this.codingBundles = _.sortBy(bundles, ['label']);
      }
    });

    this.appointmentTypeService.appointmentTypes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(appointmentTypes => {
        this.appointmentTypes = appointmentTypes;
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedGroup => {
        this.group = selectedGroup;
        this.speciality = selectedGroup.groupSpeciality;
        const matchedSpeciality = SPECIALITIES.find(s =>
          s.value === this.speciality
        );

        this.disciplineCode = matchedSpeciality?.discipline;
        this.rateType = this.disciplineConfigService.getRateType(this.disciplineCode || 'medical');
        console.log("Discipline code: ", this.disciplineCode, "Rate type: ", this.rateType);

        this.medpraxService.getTariffRatesByGroup(selectedGroup.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (response) => {
              if (!response || !response.tariffRates) {
                console.error("❌ No tariff rates found.");
                return;
              }
              if (selectedGroup.groupSpeciality === 'General Practice') {
                this.selectedTariffRateType = 'gpRate';
              }
              this.tariffRates = response.tariffRates;
              console.log("✅ Tariff Rates Fetched:", this.tariffRates);
            },
            (error) => {
              console.error("❌ Error fetching tariff rates:", error);
              if (error.status === 200 && typeof error.error.text === 'string') {
                console.error("❌ API returned HTML instead of JSON.");
              }
            }
          );
      });

    this.modifierEngineSubject.pipe(
      debounceTime(this.DEBOUNCE_TIME),
      takeUntil(this.destroy$)
    ).subscribe((lineIndex?: number) => {
      this._runModifierEngine(lineIndex);
    });
  }

  //TODO: If selectedGroup?.groupSpeciality === 'General Practice' then we use the GP rate otherwise we use the specialist rate

  ngOnInit(): void {
    if (!this.parentForm) {
      throw new Error('Parent form is required!');
    }

    // Ensure lineItems exists
    if (!this.parentForm.get('lineItems')) {
      this.parentForm.addControl('lineItems', this.fb.array([]));
    }

    // Remove auto-population of empty line item
    /* if (this.lineItems.length === 0) {
      this.addLineItem();
    } */

    // Show line items if they already exist (for pre-filled data)
    if (this.lineItems.length > 0) {
      this.showLineItems = true;
      console.log('Line items found on init, showing line items section');
    }

    this.parentForm.get('lineItems')?.valueChanges.subscribe(() => {
      this.lineItems.controls.forEach((group: any, index: number) => {
        const selectedType = group.get('codeType')?.value || 'procedure';
        const selectedCode = group.get('code')?.value
      });

      // Optionally, reset pcControl **only if necessary**
      if (!this.pcControl.value) {
        this.pcControl.setValue('');
      }
    });

    this.medpraxService.discount$.subscribe((discountInfo) => {
      console.log("🎯 Received discount update:", discountInfo);
      this.lineItems.controls.forEach((control, index) => {
        // Only update if the discount hasn't been manually modified
        if (!control.get('discount')?.dirty) {
          control.patchValue({
            discount: discountInfo.value,
            discountType: discountInfo.type
          });
          this.recalculatePrice(index, true);
        }
      });
    });

    // Fetch patient's ICD-10 codes
    this.patientService.currentPatient$
      .pipe(
        switchMap(selectedPatient => {
          if (selectedPatient) {
            this.patient = selectedPatient;
            this.patientIcdCodes = selectedPatient.icdCodes || [];
            this.icdChips = this.patient.icdCodes || [];
            this.planOptionCode = schemeCodeOf(this.patient.medicalPlanOptionCode) ?? undefined;
            this.isPrivatePatient = this.patient.medicalAid?.trim() === 'Private Patient' || !this.planOptionCode;
            if (this.isPrivatePatient) {
              this.planOptionCode = this.DEFAULT_PATIENT_RATES.planOptionCode;
            }
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.medpraxService.discount$.subscribe((discountValue) => {
      if (discountValue !== null) {
        this.lineItems.controls.forEach((control) => {
          if (!control.get('discount')?.value) {
            control.patchValue({ discount: discountValue });
          }
        });
      }
    });

    // Update ICD codes for existing line items
    this.lineItems.controls.forEach((control, index) => {
      const currentIcdCodes = control.get('icdCodes')?.value || [];
      // Only update if the line item has no ICD codes
      if (!currentIcdCodes.length) {
        control.patchValue({ icdCodes: [...this.patientIcdCodes] });
      }
    });

    this.themeService.isDarkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDarkMode) => {
        this.darkTheme = this.themeService.getTimepickerTheme(isDarkMode);
      });// Default to dark theme
  }

  override ngOnDestroy(): void {
    this.codingBundleService.getCodingBundlesByGroup()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  get lineItems(): FormArray {
    return this.parentForm.get('lineItems') as FormArray;
  }

  createLineItem(): FormGroup {
    return this.fb.group({
      date: [this.getDefaultDate()],
      code: [''],
      description: [''],
      quantity: [1],
      unitPrice: [0],
      cusPrice: [0],
      balance: [0],
      codeType: ['procedure'],
      discount: [0],
      discountType: ['percentage'],
      ruleTypeOverride: [null],
      providerId: [null],
      icdCodes: [[...this.patientIcdCodes]],
      units: [0],
      modifiers: [[]],
      isModifierLine: [false],
      trace: [[]],
      ruleSuggestions: [[]],
      startTime: [''],
      endTime: [''],
      assistantName: [''],
      assistantPracticeNumber: [''],
      weight: [''],
      height: [''],
      BMI: [''],
      initialRand: [0],
      initialUnits: [0],
      displayIndex: [this.lineItems.length], // Add displayIndex for ordering
      include0201Code: [true], // Checkbox for consumables to include 0201 prefix
      // Consumable-specific fields for pack size calculations
      standardPacksize: [null], // Store the standard pack size from product
      singleExitPrice: [null], // Store the single exit price (pack price)
      pricePerUnit: [null], // Calculated per-unit price (singleExitPrice / standardPacksize)
      product0201: [null], // Store product0201 flag to determine if 0201 prefix should be used
      // Unit information for display
      rxUnit: [null], // Unit of measure (CAPSULE, ML, VIAL, etc.)
      volumePerUnit: [null], // Volume per unit for materials (e.g., 4ml per vial)
      quantityUnit: [null], // Display unit for quantity field (capsules, ml, vials)
      type: [null], // 'medication' or 'material'
      presentationCode: [null], // Presentation code (CAP, VIA, INJ, etc.)
    });
  }

  // Get the default date for new line items
  private getDefaultDate(): Date {
    // If there are existing line items, use the most recently modified date
    if (this.lineItems.length > 0) {
      const dates = this.lineItems.controls.map(control => control.get('date')?.value);
      const validDates = dates.filter(date => date instanceof Date);
      if (validDates.length > 0) {
        return validDates[validDates.length - 1];
      }
    }
    // Otherwise use the issue date (this is the default for manual line items)
    return this.issueDate || new Date();
  }

  // Get year for a line item with fallback priority: line item date -> issue date -> current year
  private getYearForLineItem(index: number): string {
    // Priority 1: Get year from line item date
    const formGroup = this.getFormGroup(index);
    const lineItemDate = formGroup.get('date')?.value;
    if (lineItemDate) {
      try {
        const date = new Date(lineItemDate);
        if (!isNaN(date.getTime())) {
          return date.getFullYear().toString();
        }
      } catch (e) {
        // Invalid date, continue to next priority
      }
    }

    // Priority 2: Use issue date from parent component
    if (this.issueDate) {
      try {
        const date = new Date(this.issueDate);
        if (!isNaN(date.getTime())) {
          return date.getFullYear().toString();
        }
      } catch (e) {
        // Invalid date, continue to fallback
      }
    }

    // Priority 3: Fallback to current year
    return new Date().getFullYear().toString();
  }

  // Handle date change with confirmation dialog
  async onDateChange(newDate: Date, index: number) {
    // If there are no other items, just update the date
    if (this.lineItems.length <= 1) {
      this.getFormGroup(index).patchValue({ date: newDate });
      this.runModifierEngine();
      return;
    }

    // Show confirmation dialog using feedback service
    const confirmed = await this.feedbackService.showConfirmation(
      'Update Service Dates',
      'Would you like to update the service date for all existing items?',
      undefined,
      'Update This Item Only',
      'Update All'
    );

    if (confirmed) {
      // Update all items
      this.lineItems.controls.forEach(control => {
        control.patchValue({ date: newDate });
      });
    } else {
      // Update only the current item
      this.getFormGroup(index).patchValue({ date: newDate });
    }
    this.runModifierEngine();
  }

  addLineItem() {
    // Insert at the beginning of the array instead of pushing to the end
    this.lineItems.insert(0, this.createLineItem());
    // Initialize expanded state for new line item
    this.expandedFields[0] = false;
  }

  getFormGroup(index: number): FormGroup {
    return this.lineItems.at(index) as FormGroup;
  }

  cloneLineItem(index: number): void {

    // Get the form group to clone
    const sourceGroup = this.lineItems.at(index) as FormGroup;

    // Create a new form group with the same structure as createLineItem
    const newGroup = this.createLineItem();

    // Copy all values from source to new group
    Object.keys(sourceGroup.controls).forEach(key => {
      const control = sourceGroup.get(key);
      if (control) {
        newGroup.get(key)?.setValue(control.value);
      }
    });

    // Insert the clone after the source item
    this.lineItems.insert(index + 1, newGroup);

    // Initialize expanded state for new line item
    this.expandedFields[index + 1] = false;

    // Run the modifier engine to process the new line
    this.runModifierEngine();
  }

  deleteLineItem(index: number): void {
    this.lineItems.removeAt(index);
    this.recalculatePrice(index - 1); // Recalculate price for the previous line item
    this.runModifierEngine();
  }

  // Handle drag and drop reordering for physiotherapy practices
  drop(event: CdkDragDrop<any[]>) {
    if (this.isPhysioPractice && event.previousIndex !== event.currentIndex) {
      // Move the item in the FormArray
      moveItemInArray(this.lineItems.controls, event.previousIndex, event.currentIndex);
      
      // Update displayIndex for all items to reflect new order
      this.lineItems.controls.forEach((control, index) => {
        control.patchValue({ displayIndex: index });
      });
      
      // Recalculate prices and run modifier engine
      //this.runModifierEngine();
    }
  }

  // Update the onIcdCodesChanged method to handle changes from the ICD selector
  onIcdCodesChanged(codes: IcdCode[], index: number) {
    const formGroup = this.getFormGroup(index);
    formGroup.patchValue({
      icdCodes: codes
    });
  }

  // Update getIcdCodesForLineItem to safely handle form values
  getIcdCodesForLineItem(index: number): IcdCode[] {
    const formGroup = this.getFormGroup(index);
    const icdCodes = formGroup.get('icdCodes')?.value || [];
    return icdCodes;
  }

  handleLineItemModifierSelection(codes: ProcedureCode[], index: number) {
    console.log("Modifier Codes Changed: ", index, codes);
    const fg = this.getFormGroup(index);
    const currentModifiers = fg.get('modifiers')?.value || [];
    const newModifiers = codes.map(c => c.code);

    // Find modifiers that were removed
    const removedModifiers = currentModifiers.filter((m: CoreModifier) => !newModifiers.includes(m));

    // For each removed modifier, revert the line to its original state
    removedModifiers.forEach((modifierCode: CoreModifier) => {
      const appliedModifier = this.appliedModifiers.find(am =>
        am.lineIndex === index && am.modifierCode === modifierCode
      );

      if (appliedModifier) {
        // Revert to original values
        fg.patchValue({
          units: appliedModifier.originalUnits,
          unitPrice: appliedModifier.originalPrice,
          balance: appliedModifier.originalPrice,
          trace: [...(fg.get('trace')?.value || []), `Reverted ${modifierCode} adjustment`]
        });
      }
    });

    // Remove any applied modifiers that are no longer present
    this.appliedModifiers = this.appliedModifiers.filter(am =>
      !(am.lineIndex === index && !newModifiers.includes(am.modifierCode))
    );

    fg.patchValue({ modifiers: newModifiers });
    this.runModifierEngine();
  }

  handleStandaloneModifierSelection(item: any, index: number, deferModifierEngine: boolean = false) {
    console.log("Handle Standalone Modifier Selected: ", item, index);
    const fg = this.getFormGroup(index);
    const incomingUnitPrice = item.unitPrice || 0;
    const currentUnitPrice = Number(fg.get('unitPrice')?.value) || 0;
    const currentUnits = Number(fg.get('units')?.value) || 0;
    const currentStart = fg.get('startTime')?.value;
    const currentEnd = fg.get('endTime')?.value;
    const currentAssistant = fg.get('assistantName')?.value;
    const currentAssistantPracticeNumber = fg.get('assistantPracticeNumber')?.value;
    const currentWeight = fg.get('weight')?.value;
    const currentHeight = fg.get('height')?.value;
    const currentBMI = fg.get('BMI')?.value;

    // Preserve any non-zero / non-empty values already set on the form.
    // Bundle config never carries unitPrice or times, so incoming values are
    // typically 0 / undefined; letting those stomp over values the popup
    // (or an earlier engine run) already wrote is what caused 0008/0011/0018
    // to stay at R0 in bundle flow.
    const unitPriceToWrite = incomingUnitPrice > 0 ? incomingUnitPrice : currentUnitPrice;
    const unitsToWrite = currentUnits > 0 ? currentUnits : 0;
    const startToWrite = item.startTime || currentStart || '';
    const endToWrite = item.endTime || currentEnd || '';
    const assistantToWrite = item.assistantName || currentAssistant || '';
    const assistantPracticeNumberToWrite = item.assistantPracticeNumber || currentAssistantPracticeNumber || '';
    const weightToWrite = item.weight !== undefined && item.weight !== null ? item.weight : currentWeight;
    const heightToWrite = item.height !== undefined && item.height !== null ? item.height : currentHeight;
    const bmiToWrite = item.BMI !== undefined && item.BMI !== null ? item.BMI : currentBMI;

    fg.patchValue({
      code: item.code,
      description: item.description,
      startTime: startToWrite,
      endTime: endToWrite,
      assistantName: assistantToWrite,
      assistantPracticeNumber: assistantPracticeNumberToWrite,
      weight: weightToWrite,
      height: heightToWrite,
      BMI: bmiToWrite,
      isModifierLine: true,
      units: unitsToWrite,
      unitPrice: unitPriceToWrite,
      cusPrice: unitPriceToWrite // Set cusPrice equal to unitPrice for modifier lines
    }, { emitEvent: false });

    // Bundle expansion drives its own single synchronous engine run after
    // every line is configured, so opt-out of the debounced call in that path.
    // (isBundleExpanding also gates runModifierEngine as a safety net.)
    if (!deferModifierEngine) {
      this.runModifierEngine();
    }
  }

  async handleCodeSelection(code: any, codeType: 'procedure' | 'modifier' | 'consumable', index: number): Promise<void> {
    // Set loading state for this item
    this.loadingStates[index] = true;
    this.cdRef.detectChanges();

    try {
      const group = this.getFormGroup(index);
      const currentValues = group.value; // Store all current form values
      
      // Extract year for this line item
      const year = this.getYearForLineItem(index);
      
      let matchedGroup = this.tariffRates.find(rate => rate.id === this.priceGroup)
      if (!matchedGroup) {
        this.isUsingDefaultRates = true;
        matchedGroup = this.DEFAULT_PRICE_GROUP;
      } else {
        this.isUsingDefaultRates = false;
      }
      const planOptionCode = this.planOptionCode || matchedGroup?.defaultPlanOption;

      if (codeType === 'procedure') {
        const payload = {
          tariffCodes: [code.code],
          planOptionCode: planOptionCode,
          disciplineCode: this.disciplineCode,
          model: true,
          ascendingOrder: true,
          sortKey: 'Code'
        };

        // Fetch insured benefits for patient scheme rate
        const data = await firstValueFrom(this.medpraxService.listInsuredBenefits(this.rateType, payload, year));

        // Find the appropriate rate based on network status
        const networkRate = data.find(rate => rate.priceGroupType === 'MSR');
        const nonNetworkRate = data.find(rate => rate.priceGroupType === 'NNW');

        // Use network rate if doctor is network, otherwise use non-network rate
        const selectedRate = matchedGroup?.isNetworkDoctor ? networkRate : nonNetworkRate;
        console.log("Selected Rate: ", selectedRate);
        // Calculate patient scheme rate
        let cusPrice = 0;
        if (selectedRate) {
          cusPrice = this.ruleType === 'IH' ?
            (selectedRate.tariffRandSchemeFixedIH ||
              selectedRate.tariffRandRcfSchemeRateIH ||
              selectedRate.tariffRandCalculatedIH || 0) :
            (selectedRate.tariffRandSchemeFixedOH ||
              selectedRate.tariffRandRcfSchemeRateOH ||
              selectedRate.tariffRandCalculatedOH || 0);
        } else {
          // Fallback to old MSR lookup method if no rates found
          const msrData = await firstValueFrom(this.medpraxService.listMsr(this.rateType, payload, year));
          if (msrData && msrData.length > 0) {
            const msrRow = msrData[0];
            cusPrice = msrRow.tariffRandSchemeFixed ||
              msrRow.tariffRandRcfSchemeRate ||
              msrRow.tariffRandCalculated || 0;
          }
        }

        // Calculate doctor's tariff rate
        let unitPrice = 0;
        let units = 0;
        if (matchedGroup?.isNetworkDoctor) {
          const netData = await firstValueFrom(this.medpraxService.listSpa(this.rateType, payload, year));
          console.log('Network Data:', netData);
          if (netData && netData.length > 0) {
            const matchingRate = this.findMatchingRate(netData, matchedGroup);
            console.log('Matching Network Rate:', matchingRate);
            if (matchingRate) {
              unitPrice = this.calculateTariffPrice(
                matchingRate,
                +currentValues.units || matchingRate.numberOfUnits || 0
              );
              units = matchingRate.numberOfUnits || 0;
              console.log('Calculated Network Unit Price:', unitPrice);
            }
          }
        } else {
          const spaData = await firstValueFrom(this.medpraxService.listMsr(this.rateType, payload, year));
          console.log('SPA Data:', spaData);
          if (spaData && spaData.length > 0) {
            const matchingRate = this.findMatchingRate(spaData, matchedGroup);
            console.log('Matching SPA Rate:', matchingRate);
            if (matchingRate) {
              unitPrice = this.calculateTariffPrice(
                matchingRate,
                +currentValues.units || matchingRate.numberOfUnits || 0
              );
              units = matchingRate.numberOfUnits || 0;
              console.log('Calculated SPA Unit Price:', unitPrice);
            }
          }
        }

        // Fallback: If no matching rate found, use patient's scheme rate as doctor's rate
        if (unitPrice === 0 && cusPrice > 0) {
          unitPrice = cusPrice;
          console.log('Fallback Rate Applied: Using patient scheme rate as doctor rate =', unitPrice);
        }

        if (this.isPrivatePatient) {
          cusPrice = 0;
        }

        // PMB Logic: If isPMB is true, set doctor's rate to 300% of patient rate
        if (this.isPMB && cusPrice > 0) {
          unitPrice = cusPrice * 3; // 300% of patient rate
          console.log('PMB Rate Applied: Patient Rate =', cusPrice, 'Doctor Rate =', unitPrice);
        }

        // PMB Logic: If isPMB is true but no patient rate available, set doctor's rate to 300% of calculated doctor rate
        if (this.isPMB && cusPrice <= 0 && unitPrice > 0) {
          unitPrice = unitPrice * 3; // 300% of doctor's calculated rate
          console.log('PMB Rate Applied (No Patient Rate): Doctor Rate =', unitPrice);
        }

        /* if (this.isUsingDefaultRates) {
          unitPrice = 0;
        } */

        const priceOverride = this.getPriceOverride(code.code);
        unitPrice = priceOverride || unitPrice;

        // Update form with both rates
        group.patchValue({
          ...currentValues,
          code: code.code,
          description: currentValues.description || code.description,
          cusPrice: this.formatPrice(cusPrice),
          unitPrice: this.formatPrice(unitPrice),
          initialRand: this.formatPrice(unitPrice),
          initialUnits: units,
          units: units,
          codeType: codeType,
        });

        // Auto-populate time fields if this is a physio practice and no times are set
        if (this.isPhysioPractice && !currentValues.startTime && !currentValues.endTime) {
          // Get times from the parent form (dispute form)
          const parentForm = this.parentForm;
          const formStartTime = parentForm.get('startTime')?.value;
          const formEndTime = parentForm.get('finishTime')?.value;

          if (formStartTime || formEndTime) {
            group.patchValue({
              startTime: formStartTime || '',
              endTime: formEndTime || ''
            });
          }
        }

        this.recalculatePrice(index);

      } else if (codeType === 'consumable') {
        // Handle consumable items (NAPPI codes)
        // Extract year for this line item
        const year = this.getYearForLineItem(index);
        // Strip "0201 - " prefix if present before API call
        const rawNappiCode = code.code.replace(/^0201 - /, '');
        const nappiData = await firstValueFrom(this.medpraxService.getNappiPrice([rawNappiCode], year));
        if (nappiData && nappiData.length > 0) {
          const nappiRow = nappiData[0];
          const cusPrice = this.calculateNappiPrice(nappiRow);
          const unitPrice = cusPrice; // For consumables, both rates are the same

          // Get checkbox value - preserve existing value or default to true
          const currentInclude0201Code = group.get('include0201Code')?.value;
          const include0201Code = currentInclude0201Code !== undefined && currentInclude0201Code !== null 
            ? currentInclude0201Code 
            : true; // Default to true if not set
          
          // Add "0201 - " prefix to consumable codes based on checkbox
          const consumableCode = include0201Code ? `0201 - ${rawNappiCode}` : rawNappiCode;

          group.patchValue({
            ...currentValues,
            code: consumableCode,
            description: currentValues.description || nappiRow.description,
            cusPrice: this.formatPrice(cusPrice),
            unitPrice: this.formatPrice(unitPrice),
            initialRand: this.formatPrice(unitPrice),
            initialUnits: 0,
            codeType: codeType,
            include0201Code: include0201Code, // Preserve checkbox state (defaults to true)
          });

          this.recalculatePrice(index);
        }
      }
    } catch (error) {
      console.error('Error in handleCodeSelection:', error);
    } finally {
      this.loadingStates[index] = false;
      this.cdRef.detectChanges();
    }
  }

  getPriceOverride(code: string): number {
    if (this.group.tariffOverrides && this.group.tariffOverrides.length > 0) {
      const matchingOverrides = this.group.tariffOverrides.filter(
        (override: ProcedureCode) => override.code === code
      );

      if (matchingOverrides.length === 0) {
        return 0;
      }

      if (matchingOverrides.length === 1) {
        return matchingOverrides[0].priceOverride;
      }

      // If we have an appointment, try to match by appointmentTypeId
      if (this.appointment?.appointmentTypeId) {
        const matchingOverride = matchingOverrides.find(
          (override: ProcedureCode) => override.appointmentTypeId === this.appointment.appointmentTypeId
        );
        if (matchingOverride) {
          return matchingOverride.priceOverride;
        }
      }

      // If we couldn't match or no appointment was provided, show selection dialog
      this.showPriceOverrideSelectionDialog(matchingOverrides);
      return 0; // Will be updated when user makes selection
    }
    return 0;
  }

  getConsumablePriceOverride(lineCode: string): number {
    const tag = '[ConsumableOverride]';
    console.log(tag, 'lookup', { lineCode, hasGroup: !!this.group, overrideCount: this.group?.consumableOverrides?.length ?? 0 });
    if (!this.group?.consumableOverrides?.length) {
      console.log(tag, 'no overrides on group, returning 0');
      return 0;
    }
    const rawCode = this.normalizeConsumableCodeForOverrideLookup(lineCode);
    console.log(tag, 'normalized line code', { rawCode, storedCodes: this.group.consumableOverrides.map((o: { code: string; priceOverride: number }) => ({ code: o.code, priceOverride: o.priceOverride })) });
    if (!rawCode) {
      console.log(tag, 'empty rawCode, returning 0');
      return 0;
    }
    const override = this.group.consumableOverrides.find((o: { code: string }) => {
      const overrideCode = this.normalizeConsumableCodeForOverrideLookup(String(o?.code ?? ''));
      return overrideCode && (overrideCode === rawCode || overrideCode.toLowerCase() === rawCode.toLowerCase());
    });
    const result = override?.priceOverride ?? 0;
    console.log(tag, 'match result', { matched: !!override, priceOverride: result, overrideDescription: override?.description ?? override?.code });
    return result;
  }

  /** Normalize consumable code to raw NAPPI for override lookup (strip "0201 - " prefix, trim). */
  private normalizeConsumableCodeForOverrideLookup(code: string): string {
    return (code || '').replace(/^0201\s*-\s*/i, '').trim();
  }

  private showPriceOverrideSelectionDialog(overrides: ProcedureCode[]) {
    this.matchingPriceOverrides = overrides.map(override => ({
      ...override,
      appointmentType: this.appointmentTypes.find(type => type.id === override.appointmentTypeId)
    }));
    console.log("Appointment Types: ", this.appointmentTypes);
    console.log("Matching Price Overrides: ", this.matchingPriceOverrides);
    this.selectedPriceOverride = undefined;

    this.priceOverrideDialogRef = this.dialog.open(this.priceOverrideSelectionDialog, {
      disableClose: true
    });

    const sub = this.priceOverrideDialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Update the price in the form
        const lineItemIndex = this.lineItems.controls.findIndex(
          control => control.get('code')?.value === result.code
        );
        if (lineItemIndex !== -1) {
          this.lineItems.at(lineItemIndex).patchValue({
            unitPrice: result.priceOverride
          });
          this.recalculatePrice(lineItemIndex);
        }
      }
      sub.unsubscribe();
    });
  }

  /* getAppointmentTypeName(appointmentTypeId: string): string {
    console.log("Appointment Types: ", this.appointmentTypes);
    return this.appointmentTypes.find(type => type.id === appointmentTypeId)?.name || '';
  } */

  confirmPriceOverrideSelection() {
    if (this.selectedPriceOverride && this.priceOverrideDialogRef) {
      this.priceOverrideDialogRef.close(this.selectedPriceOverride);
    }
  }

  cancelPriceOverrideSelection() {
    if (this.priceOverrideDialogRef) {
      this.priceOverrideDialogRef.close();
    }
  }

  @ViewChild('priceOverrideSelectionDialog') priceOverrideSelectionDialog!: TemplateRef<any>;
  @ViewChild('codeTimeDialog') codeTimeDialog?: TemplateRef<any>;
  @ViewChild('codeBMIDialog') codeBMIDialog?: TemplateRef<any>;
  @ViewChild('codeAssistantDialog') codeAssistantDialog?: TemplateRef<any>;

  // Properties for additional info dialogs
  timeAdditionalInfo: TimeDetails = { startTime: '', endTime: '' };
  bmiAdditionalInfo: BMIDetails = { weight: 0, height: 0, BMI: '' };
  assistantAdditionalInfo: AssistantDetails = { assistantName: '', assistantPracticeNumber: '' };
  previousAssistants: AssistantOption[] = [];
  filteredAssistants: AssistantOption[] = [];
  darkTheme!: NgxMaterialTimepickerTheme;

  findMatchingRate(data: any[], priceGroup: any): any {
    console.log("[DATA]: ", data);
    console.log("[PRICE GROUP]: ", priceGroup);
    if (!data || !priceGroup) return null;

    const pgData = priceGroup.priceGroup;
    const effectiveRuleType = this.getFormGroup(this.currentIndex).get('ruleTypeOverride')?.value || this.ruleType;

    // First filter by price group code
    let matchingPriceGroupRates = data.filter(rate =>
      rate.priceGroupCode === pgData.code
    );

    if (!matchingPriceGroupRates.length) {
      console.warn("No matching price group rates found for:", pgData.code);
      matchingPriceGroupRates = data;
    }

    // Find the rate with the matching rule type
    let matchedRate = matchingPriceGroupRates.find(rate =>
      rate.ruleType === effectiveRuleType
    );

    if (!matchedRate) {
      console.warn(`No matching rate found for rule type: ${effectiveRuleType}`);
      // Fallback to OH rate if available else the first one in the list
      matchedRate = matchingPriceGroupRates.find(rate => rate.ruleType === 'OH') || matchingPriceGroupRates[0];
    }

    if (!matchedRate) {
      matchedRate = data.find(rate => priceGroup.isNetworkDoctor ? rate.priceGroupType === 'MSR' : rate.priceGroupType === 'NNW');
    }

    return matchedRate;
  }

  handleGlobalSearchSelection(item: any): void {
    console.log("Selected Item: ", item);

    // Show line items when an item is selected
    this.showLineItems = true;

    // Get current discount settings
    let currentDiscount = { value: 0, type: 'percentage' as 'percentage' | 'fixed' };
    this.medpraxService.discount$.pipe(take(1)).subscribe(discount => {
      currentDiscount = discount;
    });

    // Create a new line item
    const newItem = this.createLineItem();

    // Set the code type based on the selected item's type
    const codeType = item.type === 'procedure' ? 'procedure' : 'consumable';

    // For medicines and materials, use the consumable pricing rules engine
    if (codeType === 'consumable') {
      // Determine quantity unit and default quantity based on product type
      const { defaultQuantity, quantityUnit, volumePerUnit } = this.determineQuantityUnit(item);

      // Add "0201 - " prefix to consumable codes based on checkbox (default: true)
      const nappiFromItem = (item.nappiCode ?? item.NappiCode ?? item.catalogueNumber ?? item.CatalogueNumber ?? '').toString();
      const rawNappiCode = nappiFromItem.replace(/^0201\s*-\s*/i, '').trim();
      const product0201Flag = item.product0201 || 0;
      // Always default to true (ticked by default) - user can uncheck if needed
      const include0201Code = true;
      const consumableCode = include0201Code ? `0201 - ${rawNappiCode}` : rawNappiCode;

      // Build consumable line for pricing engine
      const consumableLine: ConsumableLine = {
        code: consumableCode,
        description: item.name,
        codeType: 'consumable',
        quantity: defaultQuantity,
        unitPrice: 0, // Will be calculated by pricing engine
        cusPrice: 0,
        balance: 0,
        standardPacksize: item.standardPacksize || 1,
        singleExitPrice: item.singleExitPrice || 0,
        pricePerUnit: 0, // Will be calculated
        product0201: product0201Flag,
        include0201Code: include0201Code,
        type: item.type,
        rxUnit: item.rxUnit,
        presentationCode: item.presentationCode,
        product: item
      };

      // Build pricing context
      const matchedGroup = this.tariffRates.find(rate => rate.id === this.priceGroup);
      const pricingContext: ConsumablePricingContext = {
        priceGroup: this.priceGroup,
        isNetworkDoctor: matchedGroup?.isNetworkDoctor || false,
        modifier: matchedGroup?.modifier || 100,
        vatRate: vatRateForGroup(this.group),
        isEDL: item.edlFlag || false,
        isOncology: item.oncologyFlag === 1
      };

      // Calculate price using the pricing rules engine
      const pricingResult = this.consumablePricingService.calculatePrice(consumableLine, pricingContext);
      const consumableOverride = this.getConsumablePriceOverride(consumableCode);
      const unitPriceToUse = consumableOverride > 0 ? consumableOverride : pricingResult.calculatedUnitPrice;
      console.log('[ConsumableOverride] apply', {
        consumableCode,
        enginePrice: pricingResult.calculatedUnitPrice,
        consumableOverride,
        unitPriceToUse,
        source: consumableOverride > 0 ? 'override' : 'engine'
      });

      newItem.patchValue({
        code: consumableCode,
        description: item.name,
        codeType: codeType,
        unitPrice: this.formatPrice(unitPriceToUse),
        cusPrice: this.formatPrice(unitPriceToUse),
        quantity: defaultQuantity,
        discount: currentDiscount.value,
        discountType: currentDiscount.type,
        include0201Code: include0201Code,
        // Store pack size information for quantity-based recalculation
        standardPacksize: consumableLine.standardPacksize,
        singleExitPrice: consumableLine.singleExitPrice,
        // Calculate base price per unit (before markup) for recalculation
        pricePerUnit: matchedGroup && !matchedGroup.isNetworkDoctor && matchedGroup.modifier
          ? pricingResult.calculatedUnitPrice / (matchedGroup.modifier / 100)
          : pricingResult.calculatedUnitPrice, // Base price before markup (VAT is applied separately at invoice level)
        product0201: product0201Flag,
        type: consumableLine.type,
        rxUnit: consumableLine.rxUnit,
        presentationCode: consumableLine.presentationCode,
        volumePerUnit: volumePerUnit,
        quantityUnit: quantityUnit
      });
    } else {
      // For procedure codes, just set the basic info - price will be fetched in handleCodeSelection
      newItem.patchValue({
        code: item.code,
        description: item.description,
        codeType: codeType,
        discount: currentDiscount.value,
        discountType: currentDiscount.type
      });
    }

    // Insert at the beginning of the array instead of pushing
    this.lineItems.insert(0, newItem);

    // Only call handleCodeSelection for procedure codes
    if (codeType === 'procedure') {
      if (this.appDataService.isCodeAModifier(item, this.group?.groupSpeciality)) {
        this.handleStandaloneModifierSelection(item, 0);
      } else {
        this.handleCodeSelection(item, codeType, 0);
      }
    } else {
      // For consumables, recalculate price immediately since we already have it
      this.recalculatePrice(0);
    }
  }

  async handleCodingBundleSelection(bundle: any, overrideDate?: Date) {
    // overrideDate: Optional date to use for all line items instead of the default date
    if (!bundle || !bundle.procedureCodes) return;

    // If there are existing line items, ask for confirmation
    if (this.lineItems.length > 0) {
      const confirmed = await this.feedbackService.showConfirmation(
        'Clear existing items?',
        'This will remove all existing line items. Do you want to continue?',
        undefined,
        'Don\'t Clear',
        'Clear Items'
      );

      if (confirmed) {
        while (this.lineItems.length) {
          this.lineItems.removeAt(0);
        }
      }
    }

    // Gate all debounced engine runs for the duration of bundle expansion.
    // See isBundleExpanding comment for rationale.
    this.isBundleExpanding = true;

    try {

    // Separate modifier and non-modifier codes
    const nonModifierCodes = bundle.procedureCodes.filter((item: any) => !this.appDataService.isCodeAModifier(item, this.group?.groupSpeciality));
    const modifierCodes = bundle.procedureCodes.filter((item: any) => this.appDataService.isCodeAModifier(item, this.group?.groupSpeciality));

    // First process all non-modifier lines
    for (const item of nonModifierCodes) {
      const lineItem = this.createLineItem();

      // Set initial values
      lineItem.patchValue({
        code: item.code,
        description: item.description,
        quantity: item.quantity || 1,
        codeType: item.codeType || 'procedure',
        date: overrideDate || this.getDefaultDate(),
        discount: 0,
        discountType: 'percentage',
        ruleTypeOverride: null
      });

      // Add ICD codes if present
      if (item.icdCodes && item.icdCodes.length > 0) {
        lineItem.patchValue({
          icdCodes: item.icdCodes
        });
      }
      this.lineItems.push(lineItem);

      // Check if this code requires additional info prompting
      const lineIndex = this.lineItems.length - 1;

      // Check for additional details and prompt if needed
      await this.checkAndPromptForAdditionalDetails(item, lineItem);

      // Handle code selection to fetch rates
      await this.handleCodeSelection(item, item.codeType || 'procedure', lineIndex);
    }

    // NOTE: the old code ran the modifier engine here, before the modifier
    // lines existed in the form. Its 1s debounce would then expire mid-popup
    // and produce a junk calc against a half-built form, which downstream
    // `handleStandaloneModifierSelection` calls couldn't always recover from.
    // We now defer the engine until every modifier line is fully configured.

    // Then process modifier lines
    for (const item of modifierCodes) {
      const lineItem = this.createLineItem();

      // Set initial values
      lineItem.patchValue({
        code: item.code,
        description: item.description,
        quantity: item.quantity || 1,
        codeType: item.codeType || 'procedure',
        date: overrideDate || this.getDefaultDate(),
        discount: 0,
        discountType: 'percentage',
        ruleTypeOverride: null
      });

      // Add ICD codes if present
      if (item.icdCodes && item.icdCodes.length > 0) {
        lineItem.patchValue({
          icdCodes: item.icdCodes
        });
      }
      this.lineItems.push(lineItem);

      // Check for additional details and prompt if needed
      await this.checkAndPromptForAdditionalDetails(item, lineItem);

      // Handle standalone modifier selection — defer the per-line engine
      // call; we'll run it once below with every line fully configured.
      this.handleStandaloneModifierSelection(item, this.lineItems.length - 1, true);
    }

    this.showLineItems = true;
    this.cdRef.detectChanges();

    } finally {
      // Clear the guard so post-bundle user edits behave normally.
      this.isBundleExpanding = false;
    }

    // Drive one synchronous engine run now that every non-modifier line is
    // priced (Medprax awaited above) and every modifier line has its final
    // startTime/endTime/isModifierLine flags. Bypassing the debounced
    // subject guarantees we don't race against an in-flight popup.
    this._runModifierEngine();
  }

  saveNappi(selectedCode: any, index: number) {
    const formGroup = this.getFormGroup(index);
    // Get checkbox value (default to true if not set)
    const include0201Code = formGroup.get('include0201Code')?.value !== false;
    
    // Add "0201 - " prefix to consumable codes based on checkbox
    const rawNappiCode = selectedCode.nappiCode.replace(/^0201 - /, '');
    const consumableCode = include0201Code ? `0201 - ${rawNappiCode}` : rawNappiCode;
    
    formGroup.patchValue({
      code: consumableCode,
      description: selectedCode.name,
      include0201Code: include0201Code // Ensure checkbox is set
    });
    if (selectedCode !== '') {
      // this.addNappiCode(selectedCode.nappiCode);
      setTimeout(() => {
        this.recalculatePrice(index);
      }, 10);
    }
  }

  // Handle checkbox change for 0201 code prefix
  onInclude0201CodeChange(index: number): void {
    const formGroup = this.getFormGroup(index);
    const include0201Code = formGroup.get('include0201Code')?.value || false;
    const currentCode = formGroup.get('code')?.value || '';
    
    // Strip existing prefix if present
    const rawNappiCode = currentCode.replace(/^0201 - /, '');
    
    // Update code based on checkbox
    const newCode = include0201Code ? `0201 - ${rawNappiCode}` : rawNappiCode;
    formGroup.patchValue({ code: newCode });
  }

  saveProcedure(selectedCode: any, index: number) {
    const formGroup = this.getFormGroup(index);
    const payload = {
      tariffCodes: [selectedCode.code],
      planOptionCode: this.planOptionCode,
      disciplineCode: this.disciplineCode,
      model: true,
      ascendingOrder: true,
    }
    console.log("Payload: ", payload);

    formGroup.patchValue({
      code: selectedCode.code,
      description: selectedCode.description
    });

    if (selectedCode !== '') {
      console.log("Full Tariffs:", this.tariffs);

      this.recalculatePrice(index);
    }
  }

  /* onTariffModifierChange(event: any, index: number) {
    const formGroup = this.getFormGroup(index);
    formGroup.patchValue({ tariffModifier: event.value });
  
    // Recalculate the price when modifier is changed
    this.recalculatePrice(index);
  } */

  recalculatePrice(index: number, recalculateShortfall: boolean = false, skipModifierEngine: boolean = false) {
    const formGroup = this.getFormGroup(index);
    const currentValues = formGroup.value;
    const codeType = formGroup.get('codeType')?.value;

    // For consumables, use the pricing rules engine when quantity changes
    if (codeType === 'consumable' && currentValues.standardPacksize && currentValues.singleExitPrice) {
      // Build product object if we have the necessary fields
      const product = currentValues.product || (currentValues.volumePerUnit ? {
        volumeMetric3: currentValues.volumePerUnit?.toString(),
        unitOfMeasure3: currentValues.quantityUnit === 'ml' ? 'ML' : undefined,
        type: currentValues.type,
        presentationCode: currentValues.presentationCode,
        rxUnit: currentValues.rxUnit
      } : undefined);

      const consumableLine: ConsumableLine = {
        code: currentValues.code || '',
        description: currentValues.description || '',
        codeType: 'consumable',
        quantity: lineQuantity(formGroup.get('quantity')?.value),
        unitPrice: 0,
        cusPrice: 0,
        balance: 0,
        standardPacksize: currentValues.standardPacksize,
        singleExitPrice: currentValues.singleExitPrice,
        pricePerUnit: currentValues.pricePerUnit || 0,
        product0201: currentValues.product0201 || 0,
        include0201Code: currentValues.include0201Code !== false,
        type: currentValues.type,
        rxUnit: currentValues.rxUnit,
        presentationCode: currentValues.presentationCode,
        product: product
      } as any; // Add volumePerUnit to the line for volume-based pricing
      
      // Add volumePerUnit to the line object for volume-based pricing rule
      (consumableLine as any).volumePerUnit = currentValues.volumePerUnit;

      // Build pricing context
      const matchedGroup = this.tariffRates.find(rate => rate.id === this.priceGroup);
      const pricingContext: ConsumablePricingContext = {
        priceGroup: this.priceGroup,
        isNetworkDoctor: matchedGroup?.isNetworkDoctor || false,
        modifier: matchedGroup?.modifier || 100,
        vatRate: vatRateForGroup(this.group)
      };

      // Calculate price using the pricing rules engine (used when no override or user price)
      const pricingResult = this.consumablePricingService.calculatePrice(consumableLine, pricingContext);

      // Prefer current form unit price (user edit), then consumable override, then engine
      const overridePrice = this.getConsumablePriceOverride(currentValues.code || '');
      const currentUnitPrice = Number(formGroup.get('unitPrice')?.value || 0);
      const unitPriceToUse = currentUnitPrice > 0
        ? currentUnitPrice
        : (overridePrice > 0 ? overridePrice : pricingResult.calculatedUnitPrice);

      // Get discount values
      const discount = Number((formGroup.get('discount')?.value || 0));
      const discountType = formGroup.get('discountType')?.value || 'percentage';

      // Calculate total from chosen unit price (not always engine total), on the quantity the server will store
      const quantity = lineQuantity(formGroup.get('quantity')?.value);
      let total = unitPriceToUse * quantity;
      if (discount > 0) {
        if (discountType === 'percentage') {
          total = total * (1 - discount / 100);
        } else {
          total = total - discount;
        }
      }

      // Update form with calculated values; preserve override or user-entered unit price
      const preserveInclude0201Code = currentValues.include0201Code !== false;
      
      formGroup.patchValue({
        ...currentValues,
        quantity,
        unitPrice: this.formatPrice(unitPriceToUse),
        cusPrice: this.formatPrice(unitPriceToUse),
        balance: this.formatPrice(total),
        include0201Code: preserveInclude0201Code // Preserve checkbox state (defaults to true)
      }, { emitEvent: false });

      if (recalculateShortfall) {
        this.cdRef.detectChanges();
      }

      // Recalculate overall totals
      this.calculateOverallTotals();
      if (!skipModifierEngine) {
        this.runModifierEngine();
      }
      return;
    }

    // For non-consumables, use the original calculation logic
    // Ensure all price values are properly formatted with 2 decimal places
    const unitPrice = Number((formGroup.get('unitPrice')?.value || 0));
    const cusPrice = Number((formGroup.get('cusPrice')?.value || 0));
    const quantity = lineQuantity(formGroup.get('quantity')?.value);
    const discount = Number((formGroup.get('discount')?.value || 0));
    const discountType = formGroup.get('discountType')?.value || 'percentage';

    console.log('Recalculate Price Inputs:', {
      index,
      unitPrice,
      cusPrice,
      quantity,
      discount,
      discountType,
      currentValues
    });

    // Calculate base total
    let total = unitPrice * quantity;
    console.log('Base Total:', total);

    // Apply discount
    if (discount > 0) {
      if (discountType === 'percentage') {
        total = total * (1 - discount / 100);
        console.log('After Percentage Discount:', total);
      } else {
        total = total - discount;
        console.log('After Fixed Discount:', total);
      }
    }

    // Update form values with 2 decimal places
    const finalBalance = this.formatPrice(total);
    const finalUnitPrice = this.formatPrice(unitPrice);
    const finalCusPrice = this.formatPrice(cusPrice);

    console.log('Final Values:', {
      finalBalance,
      finalUnitPrice,
      finalCusPrice,
      total
    });

    // Update form with formatted values
    formGroup.patchValue({
      ...currentValues,
      quantity,
      balance: finalBalance,
      unitPrice: finalUnitPrice,
      cusPrice: finalCusPrice
    }, { emitEvent: false }); // Prevent infinite loop

    if (recalculateShortfall) {
      this.cdRef.detectChanges();
    }

    // Recalculate overall totals
    this.calculateOverallTotals();
    // Run the modifier engine only if not called from within the modifier engine
    if (!skipModifierEngine) {
      this.runModifierEngine();
    }
  }

  // Add a new method to format price values
  formatPrice(value: number | null | undefined): string {
    return roundMoney(value || 0).toFixed(2);
  }

  calculateTariffPrice(tariff: any, effectiveUnits: number): number {
    // ── 1. pick the published Rand value ───────────────────────────────
    let basePrice = tariff.tariffRandSchemeFixed || 0;
    if (basePrice <= 1) {
      basePrice = tariff.tariffRandRcfSchemeRate || 0;
      if (basePrice <= 1) {
        basePrice = tariff.tariffRandCalculated || 0;
      }
    }

    // ── 2. convert to **per-unit** rate ────────────────────────────────
    const rawUnits = tariff.numberOfUnits || 1;     // Medprax "published" units
    let unitRate = basePrice / rawUnits;          // RCF = Rand-per-unit

    // ── 3. apply doctor-specific markup if NOT a network doctor ───────
    const matchedGroup = this.tariffRates.find(r => r.id === this.priceGroup);
    if (matchedGroup && !matchedGroup.isNetworkDoctor) {
      const multiplier = (matchedGroup.modifier || 100) / 100; // e.g. 217 % → 2.17
      unitRate *= multiplier;
    }

    // ── 4. multiply by **effective** (engine-adjusted) units ───────────
    return Number(unitRate * effectiveUnits)
  }


  calculateCustomerPrice(tariff: any): number {
    let basePrice = tariff.tariffRandSchemeFixed || 0;
    if (basePrice <= 1) {
      basePrice = tariff.tariffRandCalculated || 0;
    }
    return basePrice;
  }



  // Calculate Price for NAPPI Code
  calculateNappiPrice(product: any): number {
    console.log("Product Details: ", product.singleExitPrice)
    const singleExitPrice = product.singleExitPrice || 0;
    const standardPackSize = product.standardPacksize || 1;
    const dispensingVolume = product.dispensingVolumePacksize || standardPackSize;
    const pricePerUnit = singleExitPrice / standardPackSize;
    return (pricePerUnit * dispensingVolume);
  }

  /**
   * Determine the quantity unit and default quantity for a consumable item
   * Uses presentationCode/presentationDescription as primary source, with special handling for volume-based items
   * Uses standardPacksize and dispensingVolumePacksize/volumeMetric3 for volume calculations
   */
  determineQuantityUnit(item: any): { defaultQuantity: number; quantityUnit: string; volumePerUnit: number | null } {
    const standardPacksize = item.standardPacksize || 1;
    const presentationCode = item.presentationCode || '';
    const presentationDescription = item.presentationDescription || '';
    const type = item.type || 'medication';
    const rxUnit = (item.rxUnit || '').toUpperCase();
    const volumeMetric3 = item.volumeMetric3;
    const unitOfMeasure3 = item.unitOfMeasure3;
    const dispensingVolumePacksize = item.dispensingVolumePacksize;
    const itemName = (item.name || '').toUpperCase();

    // Priority 1: Check for volume-based items (ML) - these need special handling
    // Check if rxUnit is ML OR if we have volumeMetric3 with ML unit
    if (rxUnit.includes('ML') || rxUnit === 'ML' || (volumeMetric3 && unitOfMeasure3 === 'ML')) {
      // Determine volume per unit from available sources
      let volumePerUnit = 0;
      
      // Priority: volumeMetric3 > dispensingVolumePacksize
      if (volumeMetric3 && unitOfMeasure3 === 'ML') {
        volumePerUnit = parseFloat(volumeMetric3) || 0;
      } else if (dispensingVolumePacksize) {
        volumePerUnit = typeof dispensingVolumePacksize === 'string' 
          ? parseFloat(dispensingVolumePacksize) || 0
          : dispensingVolumePacksize;
      }
      
      // Calculate total volume in pack
      // For MACAINE: 10 vials × 4ml = 40ml total
      if (volumePerUnit > 0) {
        const totalVolumeInPack = standardPacksize * volumePerUnit;
        return {
          defaultQuantity: lineQuantity(totalVolumeInPack),
          quantityUnit: 'ml',
          volumePerUnit: volumePerUnit
        };
      }
      
      // If no volume information but it's volume-based, use pack size as ml
      return {
        defaultQuantity: standardPacksize,
        quantityUnit: 'ml',
        volumePerUnit: null
      };
    }

    // Priority 2: Check if item name contains "PACK" - use "pack" as unit
    if (itemName.includes('PACK')) {
      return {
        defaultQuantity: standardPacksize,
        quantityUnit: 'pack',
        volumePerUnit: null
      };
    }

    // Priority 3: Check presentationDescription or rxUnit for common unit types
    const descUpper = presentationDescription.toUpperCase();
    const rxUnitUpper = rxUnit;
    
    if (descUpper.includes('TABLET') || rxUnitUpper.includes('TABLET') || rxUnitUpper === 'TAB') {
      return {
        defaultQuantity: standardPacksize,
        quantityUnit: 'tablets',
        volumePerUnit: null
      };
    }
    
    if (descUpper.includes('CAPSULE') || rxUnitUpper.includes('CAPSULE') || rxUnitUpper.includes('CAP')) {
      return {
        defaultQuantity: standardPacksize,
        quantityUnit: 'capsules',
        volumePerUnit: null
      };
    }
    
    if (descUpper.includes('INHALER') || descUpper.includes('INHALATION')) {
      return {
        defaultQuantity: standardPacksize,
        quantityUnit: 'inhalers',
        volumePerUnit: null
      };
    }
    
    if (descUpper.includes('VIAL') || descUpper.includes('INJECTION') || descUpper.includes('INFUSION')) {
      return {
        defaultQuantity: standardPacksize,
        quantityUnit: 'vials',
        volumePerUnit: volumeMetric3 ? parseFloat(volumeMetric3) : null
      };
    }

    // Priority 4: Use presentationDescription if available and makes sense
    // Skip if it's too long or contains technical codes (like "MULTI COMP VARIAT 2")
    if (presentationDescription && 
        presentationDescription.length <= 25 && 
        !presentationDescription.toUpperCase().includes('VARIAT') &&
        !presentationDescription.toUpperCase().includes('MULTI COMP')) {
      const unit = presentationDescription.toLowerCase();
      return {
        defaultQuantity: standardPacksize,
        quantityUnit: unit,
        volumePerUnit: volumeMetric3 && unitOfMeasure3 === 'ML' ? parseFloat(volumeMetric3) : null
      };
    }

    // Priority 5: Use presentationCode as-is (lowercase) if it's a reasonable code
    // Skip codes that are too technical (like "MC2")
    if (presentationCode && 
        presentationCode.length <= 5 && 
        !presentationCode.match(/^[A-Z]{1,2}\d+$/)) { // Skip codes like "MC2"
      return {
        defaultQuantity: standardPacksize,
        quantityUnit: presentationCode.toLowerCase(),
        volumePerUnit: volumeMetric3 && unitOfMeasure3 === 'ML' ? parseFloat(volumeMetric3) : null
      };
    }

    // Priority 6: Fall back to rxUnit if no presentation code
    if (rxUnit && item.rxUnit) {
      return {
        defaultQuantity: standardPacksize,
        quantityUnit: item.rxUnit.toLowerCase(),
        volumePerUnit: volumeMetric3 && unitOfMeasure3 === 'ML' ? parseFloat(volumeMetric3) : null
      };
    }

    // Default: use "pack" for materials, "units" for medications
    return {
      defaultQuantity: standardPacksize,
      quantityUnit: type === 'material' ? 'pack' : 'units',
      volumePerUnit: null
    };
  }

  // Truncate Description for UI
  truncateDescription(description: string, maxLength: number = 150): string {
    return description?.length > maxLength ? `${description?.substring(0, maxLength)}...` : description;
  }

  // Check if time fields should be shown for a specific code
  shouldShowTimeFields(code: string): boolean {
    if (!code) return false;

    // Use the code prompting service to check if this code requires time fields
    return this.codePromptingService.shouldShowTimeFields(code.toString());
  }

  filterAssistants(value: string): void {
    const filterValue = (value || '').toLowerCase();
    this.filteredAssistants = this.previousAssistants.filter(assistant => assistant.name.toLowerCase().includes(filterValue));
  }

  onAssistantSelected(name: string): void {
    const selected = this.previousAssistants.find(assistant => assistant.name === name);
    if (selected?.practiceNumber) {
      this.assistantAdditionalInfo.assistantPracticeNumber = selected.practiceNumber;
    }
  }

  // Handle time field changes
  onTimeChange(index: number) {
    // Skip during bundle expansion (the bundle drives its own engine run)
    // and during an active engine run (its patchValues otherwise loop back).
    if (this.isBundleExpanding || this.isEngineRunning) return;

    const formGroup = this.getFormGroup(index);
    const startTime = formGroup.get('startTime')?.value;
    const endTime = formGroup.get('endTime')?.value;

    console.log(`Time changed for line ${index}: ${startTime} - ${endTime}`);

    // Run the modifier engine to recalculate any time-based adjustments
    this.runModifierEngine();
  }

  // Tooltip for ICD-10 Codes
  getTooltipText(icd: any): string {
    return `${icd.code} - ${icd.description}`;
  }

  // Handle Double Click (Optional Feature)
  handleDblClick(icd: any) {
    console.log('Double clicked:', icd);
  }

  private filterProcedureCodes(value: string): Observable<ProcedureCode[]> {
    return this.appDataService.searchCodes(value, 'pc', this.modifiersOnly, undefined, this.group.groupSpeciality).pipe(
      debounceTime(1000), // Adjust the debounce time as needed (in milliseconds)
      distinctUntilChanged()
    );
  }

  private filterNappiCodes(value: string): Observable<any[]> {
    return this.appDataService.searchCodes(value, 'npc', this.modifiersOnly, undefined, this.group.groupSpeciality).pipe(
      debounceTime(1000),
      distinctUntilChanged()
    );
  }

  openContactUsDialog(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const user = this.userService.user;
    this.contactMessage = { title: user?.title || '', name: user?.name || '', surname: user?.surname || '', email: user?.email || '', phone: '', message: '' };
    this.dialog.open(this.contactDialog!, { width: '600px' });
  }

  sendContactMessage(): void {

    const sub = this.contactService.sendContactEmail(this.contactMessage)
      .subscribe(
        (response: any) => {
          this.feedbackService.showToast('Your contact request has been sent successfully.');
          sub.unsubscribe();
        },
        (error) => {
          this.feedbackService.showToast('There was an error submitting your contact request. Please try again.');
          sub.unsubscribe();
        }
      );
    this.dialog.closeAll(); // Close the edit colleague dialog
  }

  toggleExpandedFields(index: number) {
    this.expandedFields[index] = !this.expandedFields[index];
  }

  calculateShortfall(index: number): string {
    const formGroup = this.getFormGroup(index);
    const unitPrice = (formGroup.get('unitPrice')?.value || 0);
    const customerPrice = (formGroup.get('cusPrice')?.value || 0);
    const quantity = lineQuantity(formGroup.get('quantity')?.value);
    const shortfall = (unitPrice - customerPrice) * quantity;
    return this.formatPrice(shortfall);
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('ngOnChanges called with changes:', Object.keys(changes));
    
    if (changes['priceGroup']) {

      let matchedGroup = this.tariffRates.find(rate => rate.id === this.priceGroup)
      this.isUsingDefaultRates = !matchedGroup;

      if (!changes['priceGroup'].firstChange) {
        // Recalculate prices for all line items when price group changes
        this.lineItems.controls.forEach((control, index) => {
          const code = control.get('code')?.value;
          const codeType = control.get('codeType')?.value;

          if (code && codeType) {
            // Re-fetch prices for the code
            this.handleCodeSelection({ code, type: codeType }, codeType, index);
          }
        });
      }

    }

    if (changes['isPMB'] && !changes['isPMB'].firstChange) {
      // Recalculate prices for all line items when PMB status changes
      this.lineItems.controls.forEach((control, index) => {
        const code = control.get('code')?.value;
        const codeType = control.get('codeType')?.value;

        if (code && codeType) {
          // Re-fetch prices for the code to apply PMB logic
          this.handleCodeSelection({ code, type: codeType }, codeType, index);
        }
      });
    }

    // Handle multiline items trigger for pre-filled data
    if (changes['multilineItemsTrigger'] && !changes['multilineItemsTrigger'].firstChange) {
      console.log('Multiline items trigger detected, refreshing component');
      console.log('Current line items count:', this.lineItems.length);
      
      // Show line items if they exist
      if (this.lineItems.length > 0) {
        this.showLineItems = true;
        console.log('Line items detected, showing line items section');
      }
      
      // Force change detection to update the UI
      this.cdRef.detectChanges();
      
      // Also trigger a manual update of the line items display
      if (this.lineItems.length > 0) {
        console.log('Line items detected, forcing UI update');
        // Force the component to re-render by triggering change detection again
        setTimeout(() => {
          this.cdRef.detectChanges();
        }, 100);
      }
    }
  }

  toggleDiscountType(index: number) {
    const formGroup = this.getFormGroup(index);
    const currentType = formGroup.get('discountType')?.value;
    const newType = currentType === 'percentage' ? 'fixed' : 'percentage';
    formGroup.patchValue({ discountType: newType });
    this.recalculatePrice(index, true);
  }

  /** The document's provider, as a line without its own provider is billed for. */
  get documentProviderLabel(): string {
    return providerLabel(this.providers.find(p => p.userId === this.providerId));
  }

  /** Who line `index` is billed for: its own provider, else the document's. */
  lineProviderLabel(index: number): string {
    const documentProvider = this.providers.find(p => p.userId === this.providerId);
    return providerLabel(lineProvider({ providerId: this.getFormGroup(index).get('providerId')?.value }, documentProvider, this.providers));
  }

  onRuleTypeOverrideChange(event: any, index: number) {
    const formGroup = this.getFormGroup(index);
    const code = formGroup.get('code')?.value;
    const codeType = formGroup.get('codeType')?.value;

    if (code && codeType) {
      // Get the effective rule type (override or default)
      const effectiveRuleType = event.value || this.ruleType;

      // Temporarily store the current rule type
      const originalRuleType = this.ruleType;

      // Set the rule type to the effective rule type for this calculation
      this.ruleType = effectiveRuleType;

      // Re-fetch prices for the code with the new rule type
      this.handleCodeSelection({ code, type: codeType }, codeType, index).then(() => {
        // Restore the original rule type
        this.ruleType = originalRuleType;

        // Recalculate the price to update both doctor's rate and patient scheme rate
        this.recalculatePrice(index);
      });
    }
  }

  calculateOverallTotals() {
    let totalDue = 0;
    let totalShortfall = 0;

    this.lineItems.controls.forEach((control, idx) => {
      const balance = Number((control.get('balance')?.value || 0));
      const shortfall = Number(this.calculateShortfall(idx));

      // Add to totals
      totalDue += balance;
      totalShortfall += shortfall;
    });

    // Line balances are VAT-inclusive, so VAT is extracted with rate/(1+rate), and only
    // charged at all when the practice is a registered VAT vendor.
    this.overallVatRate = vatRateForGroup(this.group);
    const { subtotal, vat } = splitInclusive(totalDue, this.overallVatRate);
    this.overallVat = vat;
    this.overallTotalDue = totalDue;
    this.overallSubtotal = subtotal;
    this.overallShortfall = totalShortfall;

    // Emit the updated totals to parent component with 2 decimal places
    this.updateTotals.emit({
      subtotal: this.overallSubtotal,
      vat: this.overallVat,
      vatRate: this.overallVatRate,
      totalDue: this.overallTotalDue,
      shortfall: this.overallShortfall
    });
  }

  async applyModiferRule(suggestion: any, index: number) {
    const modifiers = await this.appDataService.searchCodes(suggestion.ruleCode, 'pc', true, undefined, this.group.groupSpeciality).pipe(first()).toPromise();
    console.log("applyModiferRule Found Modifier: ", modifiers);
    if (modifiers) {
      const modifier = modifiers[0];
      // Find the procedure code selector for this specific line item
      const selector = this.procedureCodeSelectors?.toArray()[index];
      console.log("applyModiferRule Found Selector: ", selector);
      if (selector) {
        // Add the modifier code to the selector
        await selector.addChip(modifier as any);
        // The selector's codesChanged event will trigger handleLineItemModifierSelection
      } else {
        // Fallback to the original implementation if selector not found
        const formGroup = this.getFormGroup(index);
        const currentModifiers = formGroup.get('modifiers')?.value || [];
        const modifiers = [...currentModifiers, modifier];
        formGroup.patchValue({ modifiers }, { emitEvent: false });
        this.handleLineItemModifierSelection(modifiers, index);
      }
    }
  }

  // Public method to trigger the debounced engine
  private runModifierEngine(lineIndex?: number): void {
    // No-op while expanding a bundle; the bundle flow runs the engine itself
    // once at the end. Prevents runaway debounced re-runs.
    if (this.isBundleExpanding) return;
    this.modifierEngineSubject.next(lineIndex);
  }

  // Private implementation
  private _runModifierEngine(lineIndex?: number): void {
    // Atomic guard — if the engine's patchValues happen to cascade back into
    // a runModifierEngine call, the nested invocation becomes a no-op rather
    // than recursing forever.
    if (this.isEngineRunning) return;
    this.isEngineRunning = true;
    try {
    // reset applied modifiers to allow for new modifier engine run
    this.appliedModifiers = [];
    //reset line suggestions (emitEvent:false to avoid valueChanges cascade)
    this.lineItems.controls.forEach(control =>
      control.get('ruleSuggestions')?.patchValue([], { emitEvent: false })
    );

    /* ── 0. build raw claim-lines from the current form ──────────────── */
    // Convert all form controls into ClaimLine objects, preserving service date in meta
    const raw = this.buildClaimLines();

    /* ── 1. bucket by calendar-day (YYYY-MM-DD) ─────────────────────── */
    const buckets = new Map<string, { idx: number; line: ClaimLine }[]>();
    raw.forEach((line, idx) => {
      const key = line.meta?.['serviceDate']?.toString()
        ? new Date(line.meta?.['serviceDate']?.toString()).toISOString().slice(0, 10)   // "2025-06-05"
        : '__noDate__';
      (buckets.get(key) ?? buckets.set(key, []).get(key)!).push({ idx, line });
    });

    // Track adjusted lines and warnings across all buckets
    const adjPerFormIdx: Record<number, AdjustedLine> = {};
    const allWarnings: any[] = [];

    /* ── 2. analyse each bucket independently ───────────────────────── */
    buckets.forEach(entries => {
      /* keep reference to originating form row */
      // Add form index to each line for later reference
      const withIdx = entries.map(e => ({ ...e.line, __formIdx: e.idx }));

      // First pass: Calculate initial total price for non-modifier lines
      // This total is used as context for modifier rules
      // Note: buildClaimLines already filters out consumables, so all lines here are procedures
      const nonModifierLines = withIdx.filter(line => !line.isModifier);
      let totalPrice = 0;
      let totalUnits = 0;
      nonModifierLines.forEach(line => {
        const i = (line as any).__formIdx as number;
        const fg = this.getFormGroup(i);
        // Double-check this is not a consumable (safety check)
        const codeType = fg.get('codeType')?.value;
        if (codeType !== 'consumable') {
          totalPrice += +fg.get('initialRand')?.value || 0;
          totalUnits += +fg.get('initialUnits')?.value || 0;
        }
      });

      // Second pass: Process full line modifiers (0005, etc.)
      // Create context with initial total price for the modifier engine.
      // pricePerUnit is required by % modifiers (0008/0009/0029) to convert
      // the rand portion back into units; without it they would return 0.
      const ctx: ModifierContext = {
        totalPrice,
        totalUnits,
        pricePerUnit: totalUnits > 0 ? totalPrice / totalUnits : 0
      };

      // Process full line modifiers through the modifier engine with discipline awareness
      const res = this.modifierService.analyse(withIdx, ctx, this.disciplineCode);

      // Apply full line modifier adjustments to the form
      res.lines.forEach(adj => {
        const i = (adj as any).__formIdx as number;
        const fg = this.getFormGroup(i);
        
        // Skip consumables - they should not be processed by the modifier engine
        const codeType = fg.get('codeType')?.value;
        if (codeType === 'consumable') {
          return;
        }
        
        adjPerFormIdx[i] = adj;

        // Skip standalone modifiers for now - they'll be processed in the fourth pass
        if (adj.isModifier) {
          return;
        }

        // For non-modifier lines, update both units and price only if the specific modifier exists
        const lineModifiers = fg.get('modifiers')?.value || [];
        const hasModifier = lineModifiers.length > 0;

        // Convert warnings into suggestions for this line
        // This helps users understand why certain adjustments were made
        /* const lineSuggestions = res.warnings
          .filter(warning => warning.includes(adj.code))
          .map(warning => {
            const ruleCode = warning.match(/Modifier (\d{4})/)?.[1];
            if (!ruleCode) return null;
            const ruleDescription = this.appDataService.getCodeDescription(ruleCode, this.group?.groupSpeciality);
            return {
              ruleCode,
              ruleDescription,
              message: warning,
              kind: 'addition',
              unitsDelta: 0
            };
          })
          .filter(s => s !== null); */

        // 1) grab only the warnings for this code
        const warningsForCode = res.warnings.filter(w => w.includes(adj.code));

        // 2) de-dup them
        const uniqueWarnings = Array.from(new Set(warningsForCode));

        // 3) map into suggestions exactly once each
        const lineSuggestions = uniqueWarnings
          .map(warning => {
            const ruleCode = warning.match(/Modifier (\d{4})/)?.[1];
            if (!ruleCode) return null;
            return {
              ruleCode,
              ruleDescription: this.appDataService.getCodeDescription(ruleCode, this.group?.groupSpeciality),
              message: warning,
              kind: 'addition',
              unitsDelta: 0
            };
          })
          .filter(s => s !== null);

        // A standalone modifier line (6302 non-radiologist, 5104 multiple gestation, …)
        // adjusts the procedure it points at rather than itself, so that procedure row
        // carries no inline modifier to key off and `hasModifier` is false. The engine
        // only appends to `trace` when a rule actually changed a line, so a trace longer
        // than the initial entry is the signal that there is a result worth writing.
        // Without this the engine computed the reduction and the form threw it away.
        const engineAdjustedLine = (adj.trace?.length ?? 0) > 1;

        if (hasModifier) {
          // If the line has modifiers, process each modifier
          lineModifiers.forEach((modifierCode: CoreModifier) => {
            // Check if this modifier has already been applied to avoid duplicates
            const alreadyApplied = this.appliedModifiers.some(am =>
              am.lineIndex === i && am.modifierCode === modifierCode
            );

            if (!alreadyApplied) {
              // Store original values before applying modifier
              const originalUnits = fg.get('initialUnits')?.value || 0;
              const originalPrice = fg.get('initialRand')?.value || 0;

              // Apply the adjusted values from the modifier engine.
              // emitEvent:false is critical — these patches fire during the
              // engine run itself and, without the flag, would re-enqueue the
              // engine via other valueChanges subscribers and loop forever.
              fg.patchValue({
                units: adj.adjustedUnits,
                unitPrice: adj.adjustedRand,
                trace: adj.trace,
                ruleSuggestions: [...(adj.suggestions || []), ...lineSuggestions]
              }, { emitEvent: false });

              // Recalculate price to ensure discounts are properly applied
              this.recalculatePrice(i, true, true);

              // Track that this modifier has been applied
              this.appliedModifiers.push({
                lineIndex: i,
                modifierCode,
                originalUnits,
                originalPrice
              });
            }
          });
        } else if (engineAdjustedLine) {
          // Adjusted by a standalone modifier line. There is no inline modifier to
          // dedupe against, but the engine always recomputes from initialUnits, so
          // re-patching the same derived value on a later run is stable.
          // (emitEvent:false — same reasoning as the branch above)
          fg.patchValue({
            units: adj.adjustedUnits,
            unitPrice: adj.adjustedRand,
            trace: adj.trace,
            ruleSuggestions: [...(adj.suggestions || []), ...lineSuggestions]
          }, { emitEvent: false });

          this.recalculatePrice(i, true, true);
        } else {
          // If no modifiers, just update trace and suggestions
          // (emitEvent:false — same reasoning as the branch above)
          fg.patchValue({
            trace: adj.trace,
            ruleSuggestions: [...(adj.suggestions || []), ...lineSuggestions]
          }, { emitEvent: false });
        }
      });

      // Third pass: Recalculate total after full line modifiers
      // This new total will be used for standalone modifiers
      totalPrice = 0;
      totalUnits = 0;
      nonModifierLines.forEach(line => {
        const i = (line as any).__formIdx as number;
        const fg = this.getFormGroup(i);
        // Double-check this is not a consumable (safety check)
        const codeType = fg.get('codeType')?.value;
        if (codeType !== 'consumable') {
          totalPrice += +fg.get('initialRand')?.value || 0;
          totalUnits += +fg.get('initialUnits')?.value || 0;
        }
      });

      // Fourth pass: Process standalone modifiers (0008, 0011, etc.)
      // These are lines that are modifiers themselves, not just lines with modifiers
      const standaloneModifiers = withIdx.filter(line => line.isModifier);

      // Create context with updated total price for standalone modifiers.
      // pricePerUnit is recomputed from the post-pass-3 totals so 0008/0009/
      // 0029 can convert the % share back to units accurately.
      const standaloneCtx: ModifierContext = {
        totalPrice,
        totalUnits,
        pricePerUnit: totalUnits > 0 ? totalPrice / totalUnits : 0,
        lines: withIdx.map(line => {
          // For non-modifier lines, use their current adjusted values
          if (!line.isModifier) {
            const i = (line as any).__formIdx as number;
            const fg = this.getFormGroup(i);
            return {
              ...line,
              isModifier: false,  // Explicitly set to false for non-modifier lines
              adjustedUnits: fg.get('units')?.value || line.units,
              adjustedRand: fg.get('unitPrice')?.value || (line.rand ?? 0),
              trace: ['init']
            };
          }
          // For modifier lines, use their base values
          return {
            ...line,
            isModifier: true,  // Explicitly set to true for modifier lines
            adjustedUnits: line.units,
            adjustedRand: line.rand ?? 0,
            trace: ['init']
          };
        })
      };

      // Process standalone modifiers through the modifier engine with discipline awareness
      const standaloneRes = this.modifierService.analyse(standaloneModifiers, standaloneCtx, this.disciplineCode);

      console.log("standaloneRes", standaloneRes);

      // Apply standalone modifier adjustments to the form
      // Note: buildClaimLines already filters out consumables, but add safety check
      standaloneRes.lines.forEach(adj => {
        const i = (adj as any).__formIdx as number;
        const fg = this.getFormGroup(i);
        
        // Skip consumables - they should not be processed by the modifier engine
        const codeType = fg.get('codeType')?.value;
        if (codeType === 'consumable') {
          return;
        }
        
        // emitEvent:false — same reasoning as the pass-2 patch above.
        fg.patchValue({
          units: adj.adjustedUnits,
          unitPrice: adj.adjustedRand,
          trace: adj.trace
        }, { emitEvent: false });

        // Recalculate price to ensure discounts are properly applied
        this.recalculatePrice(i, true, true);
      });

      // Collect all warnings for logging
      allWarnings.push(res, standaloneRes);
    });

    // Log warnings if any exist
    if (allWarnings.length) {
      console.log('All Lines:', this.lineItems.value);
      console.warn('Modifier-engine warnings:', allWarnings);
    }

    // Recalculate overall totals after all modifier engine updates
    this.calculateOverallTotals();
    } finally {
      this.isEngineRunning = false;
    }
  }

  private buildClaimLines(): ClaimLine[] {
    // Only include procedure lines (exclude consumables) for the modifier engine
    return this.lineItems.controls
      .filter((c) => {
        const codeType = c.get('codeType')?.value;
        // Only include procedures and modifiers, exclude consumables
        return codeType !== 'consumable';
      })
      .map((c) => ({
        code: c.get('code')!.value,
        description: c.get('description')!.value,
        units: +c.get('units')!.value || 0,
        qty: +c.get('quantity')!.value || 0,
        modifiers: (c.get('modifiers')!.value || []) as CoreModifier[],
        isModifier: c.get('isModifierLine')!.value,
        meta: { discipline: this.disciplineCode, serviceDate: c.get('date')!.value },
        rand: c.get('unitPrice')!.value,
        initialRand: c.get('initialRand')!.value || c.get('unitPrice')!.value,
        initialUnits: c.get('initialUnits')!.value || c.get('units')!.value,
        startTime: c.get('startTime')!.value,
        endTime: c.get('endTime')!.value,
        weight: c.get('weight')!.value,
        height: c.get('height')!.value,
        BMI: c.get('BMI')!.value,
        isConsult: this.appDataService.isCodeAConsult(c.get('code')!.value, this.group?.groupSpeciality)
      }));
  }

  // Add a method to apply a specific rule suggestion
  applyRuleSuggestion(lineIndex: number, suggestion: any): void {
    const fg = this.getFormGroup(lineIndex);
    const currentUnits = fg.get('units')?.value || 0;

    // Update the line with new units and recalculate price
    fg.patchValue({
      units: suggestion.unitsDelta || currentUnits,
      trace: [...(fg.get('trace')?.value || []), `${suggestion.ruleCode} applied`]
    });

    // Recalculate price with new units
    this.recalculatePrice(lineIndex);
  }

  hasAnyRuleSuggestions(): boolean {
    return this.lineItems.controls.some(control => {
      const suggestions = control.get('ruleSuggestions')?.value || [];
      return suggestions.length > 0;
    });
  }

  getAllRuleSuggestions(): any[] {
    return this.lineItems.controls
      .map((control, index) => {
        const suggestions = control.get('ruleSuggestions')?.value || [];
        if (suggestions.length === 0) return null;

        return {
          index,
          code: control.get('code')?.value,
          description: control.get('description')?.value,
          suggestions
        };
      })
      .filter(item => item !== null);
  }

  openRuleSuggestionsDialog(): void {
    this.ruleDialogRef = this.dialog.open(this.ruleSuggestionsDialog!, {
      width: '600px',
      maxHeight: '80vh'
    });
  }

  async applyAllRuleSuggestions(): Promise<void> {
    const suggestions = this.getAllRuleSuggestions();

    for (const item of suggestions) {
      for (const suggestion of item.suggestions) {
        await this.applyModiferRule(suggestion, item.index);
      }
    }

    this.ruleDialogRef!.close();
  }

  navigateToSettings() {
    this.dialog.closeAll();
    // Named, not positional: index 5 meant Coding until the Documents tab was inserted on
    // 8 Sep 2026 and shifted it to Survey Template. See `settings-container.component.ts`.
    this.router.navigate(['/settings'], { queryParams: { tab: 'coding' } });
  }

  calculateBMI() {
    const weight = this.bmiAdditionalInfo.weight;
    const height = this.bmiAdditionalInfo.height / 100; // Convert height from cm to meters

    if (weight && height) {
      this.bmiAdditionalInfo.BMI = (weight / (height * height)).toFixed(2).toString();
    } else {
      this.bmiAdditionalInfo.BMI = '';
    }
  }

  /**
   * Checks if a code requires additional details and prompts the user if needed.
   * This method handles both time fields and BMI details for specific codes.
   * @param item The code item that may require additional details
   * @param lineItem The form group for the line item to update with additional details
   */
  private async checkAndPromptForAdditionalDetails(item: any, lineItem: FormGroup): Promise<void> {
    // Check for time fields
    if (this.codePromptingService.shouldShowTimeFields(item.code.toString())) {
      // Reset so the popup starts blank instead of pre-filled with whatever
      // the previous modifier left behind in this session.
      this.timeAdditionalInfo = { startTime: '', endTime: '' };
      const timeDetails = await this.codePromptingService.openStartFinishDialog(this.codeTimeDialog!);
      if (timeDetails) {
        item.startTime = timeDetails.startTime;
        item.endTime = timeDetails.endTime;
        // emitEvent:false to avoid triggering onTimeChange → runModifierEngine
        // during bundle expansion (the bundle drives its own final engine run).
        lineItem.patchValue({
          startTime: timeDetails.startTime,
          endTime: timeDetails.endTime
        }, { emitEvent: false });
      }
    }

    // Check for surgical assistant fields. Unlike the time popup, cancelling
    // here leaves the line in place — the name is informational only.
    if (this.codePromptingService.shouldShowAssistantFields(item.code.toString(), this.disciplineCode || 'medical')) {
      this.assistantAdditionalInfo = { assistantName: '', assistantPracticeNumber: '' };
      this.previousAssistants = await this.codePromptingService.getPreviousAssistants(this.group?.id);
      this.filterAssistants('');
      const assistantDetails = await this.codePromptingService.openAssistantDialog(this.codeAssistantDialog!);
      if (assistantDetails) {
        item.assistantName = assistantDetails.assistantName;
        item.assistantPracticeNumber = assistantDetails.assistantPracticeNumber;
        lineItem.patchValue({
          assistantName: assistantDetails.assistantName,
          assistantPracticeNumber: assistantDetails.assistantPracticeNumber
        }, { emitEvent: false });
      }
    }

    // Check for BMI (code 0018)
    if (item.code.toString() === '0018') {
      // Reset so the popup starts from the patient defaults instead of last
      // session's input.
      this.bmiAdditionalInfo = {
        weight: this.patient?.weight || 0,
        height: this.patient?.height || 0,
        BMI: ''
      };
      const bmiDetails = await this.codePromptingService.openBMIDialog(this.codeBMIDialog!, this.patient);
      if (bmiDetails) {
        item.weight = bmiDetails.weight;
        item.height = bmiDetails.height;
        item.BMI = bmiDetails.BMI;
        lineItem.patchValue({
          weight: bmiDetails.weight,
          height: bmiDetails.height,
          BMI: bmiDetails.BMI
        }, { emitEvent: false });
      }
    }
  }
}
